import { cache } from "react";
import { filterOptions as mockFilterOptions } from "@/lib/mock-data";
import {
  FilterOptionsData,
  Modality,
  ReasoningCapability,
  SupportedProtocol,
} from "@/types/model";
import { withApiHost } from "./config";
import { apiFetch } from "./fetch-api";

const modalityLabelFallback: Record<Modality, string> = {
  text: "文本",
  image: "图像",
  file: "文件",
  audio: "音频",
  video: "视频",
  embeddings: "向量",
};

const protocolLabelFallback: Record<SupportedProtocol, string> = {
  completions: "OpenAI Chat Completions",
  response: "OpenAI Responses",
  messages: "Anthropic Messages",
  completions_compatible: "OpenAI Chat Completions (Compatible)",
  response_compatible: "OpenAI Responses (Compatible)",
  messages_compatible: "Anthropic Messages (Compatible)",
  systemone: "SystemOne (Jev)",
};

/** 兼容旧值 / 英文长名 / 接口原始值，统一归一到 SupportedProtocol */
const protocolAliasMap: Record<string, SupportedProtocol> = {
  completion: "completions",
  completions: "completions",
  responses: "response",
  response: "response",
  message: "messages",
  messages: "messages",
  "OpenAI Chat Completions": "completions",
  "OpenAI Responses": "response",
  "Anthropic Messages": "messages",
  "Google VertexAI": "response",
  vertex: "response",
  "openai chat completions": "completions",
  "openai responses": "response",
  "anthropic messages": "messages",
  "google vertexai": "response",
  completions_compatible: "completions_compatible",
  response_compatible: "response_compatible",
  messages_compatible: "messages_compatible",
  systemone: "systemone",
  "openai chat completions compatible": "completions_compatible",
  "openai responses compatible": "response_compatible",
  "anthropic messages compatible": "messages_compatible",
};

const reasoningAliasMap: Record<string, ReasoningCapability> = {
  "1": "none",
  "2": "toggleable",
  "3": "always-on",
};

const distillationAliasMap: Record<string, "distillable" | "non-distillable"> =
  {
    "1": "distillable",
    "2": "non-distillable",
  };

const zdrAliasMap: Record<string, "support" | "non-support"> = {
  "1": "support",
  "2": "non-support",
};

const toModalityValue = (label: string): Modality | null => {
  const map: Record<string, Modality> = {
    文本: "text",
    文字: "text",
    图像: "image",
    图片: "image",
    文件: "file",
    音频: "audio",
    视频: "video",
    向量: "embeddings",
  };
  return map[label] || (label as Modality) || null;
};

const normalizeModalityList = (
  list: string[] | undefined,
  fallback: readonly Modality[],
) =>
  (list?.map(toModalityValue).filter(Boolean) as Modality[] | undefined)?.map(
    (value) => ({
      value,
      label: modalityLabelFallback[value] || value,
    }),
  ) ||
  fallback.map((value) => ({
    value,
    label: modalityLabelFallback[value] || value,
  }));

const normalizeProtocols = (
  list: string[] | undefined,
  fallback: readonly SupportedProtocol[],
) =>
  (list || fallback).map((item) => {
    const value = protocolAliasMap[item] || (item as SupportedProtocol);
    return {
      value,
      label: protocolLabelFallback[value] || item,
    };
  });

const normalizeStringOptions = (
  list: string[] | undefined,
  fallback: readonly string[],
) =>
  (list && list.length > 0 ? list : [...fallback]).map((value) => ({
    value,
    label: value,
  }));

const normalizeFromMock = (): FilterOptionsData => ({
  inputModalities: normalizeModalityList(
    mockFilterOptions.inputModalities as any,
    mockFilterOptions.inputModalities,
  ),
  outputModalities: normalizeModalityList(
    mockFilterOptions.outputModalities as any,
    mockFilterOptions.outputModalities,
  ),
  authors: normalizeStringOptions(
    mockFilterOptions.series as any,
    mockFilterOptions.series,
  ),
  series: normalizeStringOptions(
    mockFilterOptions.series as any,
    mockFilterOptions.series,
  ),
  categories: normalizeStringOptions(
    mockFilterOptions.categories as any,
    mockFilterOptions.categories,
  ),
  parameters: normalizeStringOptions(
    mockFilterOptions.parameters as any,
    mockFilterOptions.parameters,
  ),
  supportedProtocols: normalizeProtocols(
    mockFilterOptions.supportedProtocols as any,
    mockFilterOptions.supportedProtocols as any,
  ),
  providers: normalizeStringOptions(
    mockFilterOptions.providers as any,
    mockFilterOptions.providers,
  ),
});

const normalizeRemoteFilterOptions = (data: any): FilterOptionsData => {
  const authorsList = Array.isArray(data?.authors) ? data.authors : [];
  const seriesList = Array.isArray(data?.series) ? data.series : [];
  const companys = Array.isArray(data?.companys) ? data.companys : [];

  const inputModalities = normalizeModalityList(
    data?.input_modalities,
    mockFilterOptions.inputModalities,
  );

  const outputModalities = normalizeModalityList(
    data?.output_modalities,
    mockFilterOptions.outputModalities,
  );

  const supportedProtocols = normalizeProtocols(
    data?.output_protocol,
    mockFilterOptions.supportedProtocols as any,
  );

  const authors =
    authorsList.length > 0
      ? authorsList.map((author: any) => ({
          value: String(author?.id ?? ""),
          label: author?.author_name ?? "",
        }))
      : normalizeStringOptions(
          mockFilterOptions.series as any,
          mockFilterOptions.series,
        );

  const series =
    seriesList.length > 0
      ? seriesList.map((s: any) => ({
          value: String(s?.id ?? ""),
          label: s?.series_name ?? "",
        }))
      : normalizeStringOptions(
          mockFilterOptions.series as any,
          mockFilterOptions.series,
        );

  const categories = normalizeStringOptions(
    data?.scene_tag,
    mockFilterOptions.categories as any,
  );

  const parameters = normalizeStringOptions(
    data?.super_params_list,
    mockFilterOptions.parameters as any,
  );

  const providers =
    companys.length > 0
      ? companys.map((company: any) => ({
          value: String(company?.id ?? ""),
          label: company?.company ?? "",
          icon: company?.icon,
          region: company?.region,
        }))
      : normalizeStringOptions(
          mockFilterOptions.providers as any,
          mockFilterOptions.providers,
        );

  return {
    inputModalities,
    outputModalities,
    authors,
    series,
    categories,
    parameters,
    supportedProtocols,
    providers,
    reasoning:
      typeof data?.reasoning === "object"
        ? Object.entries(data.reasoning)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([key, label]) => ({
              value: reasoningAliasMap[key] ?? "none",
              label: String(label || ""),
            }))
        : undefined,
    distillation:
      typeof data?.distillation === "object"
        ? Object.entries(data.distillation)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([key, label]) => ({
              value: distillationAliasMap[key] ?? "distillable",
              label: String(label || ""),
            }))
        : undefined,
    zdr:
      typeof data?.data_retention === "object"
        ? Object.entries(data.data_retention)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([key, label]) => ({
              value: zdrAliasMap[key] ?? "support",
              label: String(label || ""),
            }))
        : undefined,
  };
};

export const DEFAULT_FILTER_OPTIONS: FilterOptionsData = normalizeFromMock();

/** 单条厂商信息（配置中一项） */
export type CompanyByIdEntry = {
  id: number;
  name: string;
  icon?: string;
  region?: number;
  company_value?: string;
};

/**
 * 厂商配置：可能是「id -> 信息」对象，也可能是「信息数组」。
 * 接口/配置有时返回数组，每项格式为 { id, name, icon, region }。
 */
export type CompanyByIdMap =
  | Record<string, CompanyByIdEntry>
  | CompanyByIdEntry[];

/** 作者 id -> 展示信息（列表接口解析 author 用） */
export type AuthorByIdMap = Record<
  string,
  { id: number; name: string; logo?: string; author_value?: string }
>;

/** 系列 id -> 展示信息（列表接口解析 series 用，头像取 icon） */
export type SeriesByIdMap = Record<
  string,
  { id: number; series_name: string; icon?: string }
>;

function normalizeSlug(s: string): string {
  return s.toLowerCase().replace(/\s+/g, "").trim();
}

/**
 * 获取供应商详情页 URL 用 slug：/providers/{id} 的 id 优先使用 company_value，无则用 name
 */
export function getCompanySlugForUrl(
  companyById: CompanyByIdMap,
  companyId: string,
  fallbackName: string,
): string {
  const c = getCompanyById(companyById, companyId) as CompanyByIdEntry | undefined;
  const val = c?.company_value?.trim();
  return val || fallbackName;
}

/** 获取作者详情页 URL 用 slug：优先 author_value，无则用 name */
export function getAuthorSlugForUrl(
  authorById: AuthorByIdMap,
  authorId: string,
  fallbackName: string,
): string {
  const a = authorById[authorId];
  const val = a?.author_value?.trim();
  return val || fallbackName;
}

/**
 * 按 id 从 companyById 中取厂商信息，兼容对象和数组两种格式。
 */
export function getCompanyById(
  companyById: CompanyByIdMap,
  id: string,
): CompanyByIdEntry | undefined {
  if (Array.isArray(companyById)) {
    return companyById.find((c) => String(c.id) === id);
  }
  return companyById[id];
}

/**
 * 根据 path 内的 slug 解析出厂商 id。优先通过 company_value 匹配，匹配不到再通过 name 匹配。
 * 兼容 companyById 为对象或数组。
 */
export function getCompanyIdBySlug(
  slug: string,
  companyById: CompanyByIdMap,
): string | null {
  const slugNorm = normalizeSlug(slug);
  const entries = Array.isArray(companyById)
    ? companyById.map((c) => ({ id: String(c.id), info: c }))
    : Object.entries(companyById).map(([id, info]) => ({ id, info }));
  // 1. 优先通过 company_value 匹配
  for (const { id, info } of entries) {
    const entry = info as CompanyByIdEntry;
    const val = entry.company_value;
    if (
      val &&
      (slug === val ||
        slugNorm === normalizeSlug(val) ||
        normalizeSlug(val).includes(slugNorm))
    ) {
      return id;
    }
  }
  // 2. 再通过 name 匹配
  for (const { id, info } of entries) {
    const name = (info as CompanyByIdEntry).name;
    if (
      name &&
      (slug === name ||
        slugNorm === normalizeSlug(name) ||
        normalizeSlug(name).includes(slugNorm))
    ) {
      return id;
    }
  }
  return null;
}

/**
 * 根据 path 内的 slug 解析出作者 id。优先通过 author_value 匹配，匹配不到再通过 name 匹配。
 */
export function getAuthorIdBySlug(
  slug: string,
  authorById: AuthorByIdMap,
): string | null {
  const slugNorm = normalizeSlug(slug);
  const entries = Object.entries(authorById);
  // 1. 优先通过 author_value 匹配
  for (const [id, info] of entries) {
    const val = info.author_value;
    if (
      val &&
      (slug === val ||
        slugNorm === normalizeSlug(val) ||
        normalizeSlug(val).includes(slugNorm))
    ) {
      return id;
    }
  }
  // 2. 再通过 name 匹配
  for (const [id, info] of entries) {
    if (
      info.name &&
      (slug === info.name ||
        slugNorm === normalizeSlug(info.name) ||
        normalizeSlug(info.name).includes(slugNorm))
    ) {
      return id;
    }
  }
  return null;
}

function buildDefModelCnfMaps(data: any): {
  companyById: CompanyByIdMap;
  authorById: AuthorByIdMap;
  seriesById: SeriesByIdMap;
} {
  const companys = Array.isArray(data?.companys) ? data.companys : [];
  const authors = Array.isArray(data?.authors) ? data.authors : [];
  const seriesList = Array.isArray(data?.series) ? data.series : [];
  const companyById: CompanyByIdMap = {};
  const authorById: AuthorByIdMap = {};
  const seriesById: SeriesByIdMap = {};
  companys.forEach((c: any) => {
    const id = String(c?.id ?? "");
    if (id)
      (companyById as Record<string, CompanyByIdEntry>)[id] = {
        id: c.id,
        name: c?.company ?? c?.name ?? "",
        icon: c?.icon,
        region: c?.region,
        company_value: c?.company_value,
      };
  });
  authors.forEach((a: any) => {
    const id = String(a?.id ?? "");
    if (id)
      authorById[id] = {
        id: a.id,
        name: a?.author_name ?? "",
        logo: a?.logo,
        author_value: a?.author_value,
      };
  });
  seriesList.forEach((s: any) => {
    const id = String(s?.id ?? "");
    if (id)
      seriesById[id] = {
        id: s.id,
        series_name: s?.series_name ?? "",
        icon: s?.icon,
      };
  });
  return { companyById, authorById, seriesById };
}

/**
 * 远程获取筛选选项，失败时由调用方决定是否使用默认值
 */
export async function fetchFilterOptions(): Promise<FilterOptionsData> {
  const response = await apiFetch(
    withApiHost("/manage_api/admin/def_model_cnf"),
    {
      cache: "no-store",
    },
  );

  if (!response.ok) {
    throw new Error(`请求筛选项失败，状态码 ${response.status}`);
  }

  const payload = await response.json();
  if (payload?.errno !== 0 || !payload?.data) {
    throw new Error("筛选项接口返回异常");
  }

  return normalizeRemoteFilterOptions(payload.data);
}

/**
 * 一次请求同时返回筛选项与 id 映射（列表接口转换时用）
 */
export async function fetchDefModelCnfWithMaps(): Promise<{
  filterOptions: FilterOptionsData;
  companyById: CompanyByIdMap;
  authorById: AuthorByIdMap;
  seriesById: SeriesByIdMap;
}> {
  const response = await apiFetch(
    withApiHost("/manage_api/admin/def_model_cnf"),
    {
      cache: "no-store",
    },
  );

  if (!response.ok) {
    throw new Error(`请求筛选项失败，状态码 ${response.status}`);
  }

  const payload = await response.json();
  if (payload?.errno !== 0 || !payload?.data) {
    throw new Error("筛选项接口返回异常");
  }

  const data = payload.data;
  const filterOptions = normalizeRemoteFilterOptions(data);
  const { companyById, authorById, seriesById } = buildDefModelCnfMaps(data);
  return { filterOptions, companyById, authorById, seriesById };
}

/** 全局配置数据类型，供 Context 与各页面使用 */
export type DefModelCnfState = {
  filterOptions: FilterOptionsData;
  companyById: CompanyByIdMap;
  authorById: AuthorByIdMap;
  seriesById: SeriesByIdMap;
};

/**
 * 服务端用：同一次请求内多次调用只发起一次请求（React cache 去重）。
 * 在 layout 中先调用以在「网站加载前」拉取配置，其他页面直接使用即可。
 */
export const getDefModelCnfCached = cache(fetchDefModelCnfWithMaps);
